import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import { useSettings } from '@/hooks/useProducts';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import QuickEditImage from '@/components/quickedit/QuickEditImage';
import ImageWithFallback from '@/components/ui/ImageWithFallback';
import SectionPhotoCredit from '@/components/homepage/SectionPhotoCredit';
import AvailabilitySearchBar from '@/components/property/AvailabilitySearchBar';
import LodgingHero from '@/components/lodging/LodgingHero';
import { SITE_TYPE } from '@/config/site';
import { resolveLook, type HeroVariant } from '@/looks';
import { cn } from '@/lib/utils';

interface HeroSectionProps {
  section: any;
  /**
   * Layout variant from the tenant's selected Look. When omitted, it's
   * resolved from `site_settings.theme_config.look` via resolveLook(), with
   * `standard` as the safe fallback (matches the pre-Looks behavior).
   */
  variant?: HeroVariant;
}

export default function HeroSection({ section, variant: variantProp }: HeroSectionProps) {
  const { data: settingsData } = useSettings();
  const resolvedVariant: HeroVariant =
    variantProp ?? resolveLook(settingsData?.settings?.theme_config?.look, SITE_TYPE).hero_variant;

  // `none` is a valid Look choice — render nothing so the next homepage
  // section becomes the page's first visible content.
  if (resolvedVariant === 'none') return null;

  const content = section.content ?? {};
  const heading = section.title || 'Grow Something Beautiful';
  const subtitle = section.subtitle || content.subtitle || 'Discover what we have to offer.';
  const ctaText = content.cta_text || 'Get Started';
  const ctaLink = content.cta_link || '/shop';
  const headingId = `hero-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  // Renders the heading/subtitle/CTA block. `palette` is 'on-image' when the
  // content sits over a photo background (dark text shadow + white text), or
  // 'on-light' when it sits on a card/split background (sage text, no shadow).
  // `cta` overrides the default CTA button (e.g. the search bar for
  // `search_forward`); pass `null` to render heading + subtitle only.
  function HeroContent({ palette, cta }: { palette: 'on-image' | 'on-light'; cta?: React.ReactNode }) {
    const headingClass = cn(
      'font-display text-4xl font-bold sm:text-5xl lg:text-6xl',
      palette === 'on-image' ? 'text-white' : 'text-sage-900',
    );
    const subtitleClass = cn(
      'mt-4 font-display italic text-lg sm:mt-6 sm:text-xl',
      palette === 'on-image' ? 'text-white/90' : 'text-sage-700',
    );
    const headingStyle = palette === 'on-image' ? { textShadow: '0 2px 12px rgba(0,0,0,0.3)' } : undefined;
    return (
      <>
        <h1 id={headingId} className={headingClass} style={headingStyle}>
          {isActive ? (
            <QuickEditInline
              value={heading}
              onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
              label="Hero heading"
              as="span"
              className={headingClass}
            />
          ) : heading}
        </h1>
        <p className={subtitleClass}>
          {isActive ? (
            <QuickEditInline
              value={subtitle}
              onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
              label="Hero subtitle"
              as="span"
              className={subtitleClass}
            />
          ) : subtitle}
        </p>
        {cta !== undefined ? (
          cta
        ) : (
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
            <Link
              to={ctaLink}
              className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-cta px-8 py-3.5 text-base font-medium text-cta-fg hover:bg-cta-deep transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-sage-900"
            >
              {ctaText}
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
        )}
      </>
    );
  }

  // QuickEdit image picker — reused across every variant that renders an image.
  // The stock credit rides along here (rather than per-variant) so every hero
  // layout carries the attribution Unsplash's terms require.
  const heroImage = (
    <QuickEditImage
      src={content.image_url}
      onSave={(url) => mutation.mutateAsync({ content: { ...section.content, image_url: url } })}
      label="Hero background image"
      targetWidth={1920}
      targetHeight={1080}
      specKey="hero.image_url"
    >
      <ImageWithFallback
        src={content.image_url}
        alt=""
        className="h-full w-full object-cover"
        fallbackIcon={false}
        fallbackClassName="bg-gradient-to-br from-sage-700 via-sage-800 to-sage-900"
      />
      <SectionPhotoCredit content={content} />
    </QuickEditImage>
  );

  // --- split: image on one side, content on the other (stacked on mobile) ---
  if (resolvedVariant === 'split') {
    return (
      <section aria-labelledby={headingId} data-hero-variant="split" className="relative overflow-hidden bg-cream">
        <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 lg:gap-0">
          <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[480px]">
            {heroImage}
          </div>
          <div className="flex items-center px-4 py-12 sm:px-6 sm:py-16 lg:px-12 lg:py-20">
            <div className="max-w-xl">
              <HeroContent palette="on-light" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // --- centered_card: bg image with content card floating centered on top ---
  if (resolvedVariant === 'centered_card') {
    return (
      <section aria-labelledby={headingId} data-hero-variant="centered_card" className="relative overflow-hidden">
        <div className="absolute inset-0">
          {heroImage}
          <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:py-40 flex justify-center">
          <div className="max-w-2xl text-center bg-cream/95 backdrop-blur-sm rounded-2xl p-8 sm:p-10 lg:p-12 shadow-xl">
            <HeroContent palette="on-light" />
          </div>
        </div>
      </section>
    );
  }

  // --- fullscreen_media: image fills viewport, large centered content over it ---
  if (resolvedVariant === 'fullscreen_media') {
    return (
      <section aria-labelledby={headingId} data-hero-variant="fullscreen_media" className="relative overflow-hidden min-h-[80vh] lg:min-h-screen flex items-center">
        <div className="absolute inset-0">
          {heroImage}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" aria-hidden="true" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-center mx-auto">
            <HeroContent palette="on-image" />
          </div>
        </div>
      </section>
    );
  }

  // --- search_forward: photo hero with an availability search bar front-and-
  // center (lodging). Rendered through LodgingHero — the same shell the
  // `availability_search` CMS section uses — so the two stay in lock-step.
  if (resolvedVariant === 'search_forward') {
    return (
      <LodgingHero
        headingId={headingId}
        variantAttr="search_forward"
        media={heroImage}
        eyebrow={content.eyebrow}
        title={isActive ? (
          <QuickEditInline
            value={heading}
            onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
            label="Hero heading"
            as="span"
          />
        ) : heading}
        subtitle={isActive ? (
          <QuickEditInline
            value={subtitle}
            onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
            label="Hero subtitle"
            as="span"
          />
        ) : subtitle}
        searchBar={<AvailabilitySearchBar targetHref={content.cta_link || '/properties'} />}
      />
    );
  }

  // --- standard ---
  return (
    <section aria-labelledby={headingId} data-hero-variant={resolvedVariant} className="relative overflow-hidden">
      <div className="absolute inset-0">
        {heroImage}
        <div className="absolute inset-0 overlay-warm" aria-hidden="true" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
        <div className="max-w-2xl text-center mx-auto lg:text-left lg:mx-0">
          <HeroContent palette="on-image" />
        </div>
      </div>
    </section>
  );
}
